import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { InstaQLEntity } from "@instantdb/react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Globe,
  Link,
  MessageCircle,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react";
import { viewChatConversationLink } from "../../../constants.ts";
import {
  approveAdminSourceRequest,
  fetchAdminAutoApprovePending,
  fetchAdminSourceEvents,
  fetchAdminSourcePreview,
  type FollowingScanResult,
  rejectAdminSourceRequest,
  scanInstagramFollowing,
  type SourcePreviewResult,
} from "../adminApi.ts";
import { Spinner } from "../components/Spinner.tsx";
import { db } from "../db.ts";
import type { EventEntry } from "../../../src/fields.ts";
import { stripTrailingNumeral } from "../../../src/groupNameUtil.ts";
import type schema from "../../../instant.schema.ts";

type SourceRequest = InstaQLEntity<typeof schema, "sourceRequests">;

const statuses = ["pending", "approved", "rejected"] as const;
type Status = typeof statuses[number];

type Toast = {
  id: number;
  kind: "info" | "success" | "error";
  title: string;
  description?: string;
  pending?: boolean;
};

const formatDate = (timestamp?: number) =>
  timestamp
    ? new Date(timestamp).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    })
    : "-";

const getBucketLabel = (timestamp?: number) => {
  if (!timestamp) return "Older";
  const now = new Date();
  const date = new Date(timestamp);
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    .getTime();
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const sevenDaysAgoStart = todayStart - 6 * 24 * 60 * 60 * 1000;
  const thirtyDaysAgoStart = todayStart - 29 * 24 * 60 * 60 * 1000;

  const t = date.getTime();
  if (t >= todayStart) {
    return "Today";
  }
  if (t >= yesterdayStart) {
    return "Yesterday";
  }
  if (t >= sevenDaysAgoStart) {
    return "This Week";
  }
  if (t >= thirtyDaysAgoStart) {
    return "This Month";
  }
  return "Older";
};

const groupClustersByDate = (clusters: SourceRequest[][]) =>
  clusters.reduce<{ label: string; clusters: SourceRequest[][] }[]>(
    (acc, cluster) => {
      const primary = cluster[0];
      const label = getBucketLabel(primary?.createdAt);
      const existing = acc.find((b) => b.label === label);
      if (existing) {
        existing.clusters.push(cluster);
      } else {
        acc.push({ label, clusters: [cluster] });
      }
      return acc;
    },
    [],
  );

const statusLabelClass: Record<Status, string> = {
  pending: "text-amber-200 border-amber-500/30 bg-amber-500/10",
  approved: "text-emerald-200 border-emerald-500/30 bg-emerald-500/10",
  rejected: "text-red-200 border-red-500/30 bg-red-500/10",
};

const kindLabel = (kind: string) =>
  kind === "whatsapp-group"
    ? "WhatsApp group"
    : kind === "facebook-source"
    ? "Facebook group/page"
    : kind === "link-scrape-source"
    ? "Website source"
    : kind === "coverage-gap"
    ? "Coverage gap"
    : "Instagram page";

const payloadSummary = (request: SourceRequest) =>
  request.kind === "instagram-page"
    ? request.instagramUsername
      ? `@${request.instagramUsername}`
      : request.sourceUrl
    : request.kind === "coverage-gap"
    ? (request.payload as { querySummary?: string })?.querySummary ||
      request.title ||
      "Coverage gap"
    : (request.payload as { title?: string })?.title ||
      request.title ||
      request.sourceUrl;

const whatsappDecision = (request: SourceRequest) =>
  request.kind === "whatsapp-group" &&
    (request.payload as { kind?: string; recommendJoin?: boolean }).kind ===
      "whatsapp-group"
    ? (request.payload as { recommendJoin: boolean }).recommendJoin
      ? "Recommended"
      : "Skip"
    : null;

const whatsappChatTitle = (request: SourceRequest) =>
  request.kind === "whatsapp-group" && request.discoverySource === "spy-bot"
    ? (request.payload as { chatTitle?: string }).chatTitle
    : undefined;

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) =>
  value === undefined || value === null || value === ""
    ? null
    : (
      <div className="text-sm text-slate-300">
        <span className="text-slate-400">{label}:</span> {String(value)}
      </div>
    );

const toastClass: Record<Toast["kind"], string> = {
  info: "border-cyan-400/30 bg-cyan-500/10 text-cyan-100",
  success: "border-emerald-400/30 bg-emerald-500/10 text-emerald-100",
  error: "border-red-400/30 bg-red-500/10 text-red-100",
};

const ToastViewport = ({
  toasts,
}: {
  toasts: Toast[];
}) => (
  <div className="pointer-events-none fixed right-4 top-4 z-50 flex w-[min(26rem,calc(100vw-2rem))] flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`rounded-xl border px-4 py-3 shadow-xl backdrop-blur-md ${
          toastClass[toast.kind]
        }`}
      >
        <div className="flex items-center gap-2">
          {toast.pending && <Spinner className="h-4 w-4" />}
          <div className="text-sm font-semibold">{toast.title}</div>
        </div>
        {toast.description && (
          <div className="mt-1 text-xs opacity-90">{toast.description}</div>
        )}
      </div>
    ))}
  </div>
);

const formatEventDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const SourceEventRow = ({ event }: { event: EventEntry }) => (
  <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 space-y-1">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div dir="auto" className="font-medium text-white truncate">
          {event.structured.name}
        </div>
        <div className="text-xs text-gray-400">
          {formatEventDate(event.structured.timestamp.start)}
        </div>
        {event.structured.venue && (
          <div className="text-xs text-gray-500">{event.structured.venue}</div>
        )}
      </div>
      {event.structured.links[0] && (
        <a
          href={event.structured.links[0]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-purple-400 hover:text-purple-300 shrink-0"
        >
          Link
        </a>
      )}
    </div>
  </div>
);

type SelectedSource = {
  label: string;
  sourceKind: "instagram-page" | "whatsapp-group" | "link-scrape-source";
  sourceRef: string;
};

const clusterKey = (request: SourceRequest) =>
  request.kind === "whatsapp-group"
    ? `wa:${stripTrailingNumeral(request.title).toLowerCase()}`
    : request.kind === "facebook-source"
    ? `fb:${request.sourceUrl}`
    : request.kind === "link-scrape-source"
    ? `link:${request.sourceUrl}`
    : request.kind === "coverage-gap"
    ? `cg:${request.id}`
    : `ig:${request.instagramUsername ?? request.sourceUrl}`;

const clusterRequests = (requests: SourceRequest[]) =>
  Object.values(
    requests.reduce<Record<string, SourceRequest[]>>((acc, request) => {
      const key = clusterKey(request);
      return { ...acc, [key]: [...(acc[key] ?? []), request] };
    }, {}),
  );

const discoverySourceLabel = (source?: string) =>
  source === "spy-bot"
    ? "Spy bot discovery"
    : source === "team-member-agent" || source === "ceo-agent"
    ? "Team member discovery"
    : source === "user-request"
    ? "User request"
    : null;

const fbPayload = (request: SourceRequest) =>
  request.kind === "facebook-source"
    ? (request.payload as {
      title?: string;
      description?: string;
      privacy?: string;
      category?: string;
    })
    : null;

const coverageGapPayload = (request: SourceRequest) =>
  request.kind === "coverage-gap"
    ? (request.payload as {
      category?: string;
      location?: string;
      querySummary?: string;
      timeContext?: string;
      notes?: string;
    })
    : null;

const getSourceRequestViewChatLink = (request: SourceRequest) => {
  const payload = request.payload as Record<string, unknown> | null | undefined;
  const conversationId = request.conversationId ||
    (payload?.conversationId as string | undefined) ||
    (payload?.chatId as string | undefined) ||
    (request.discoverySource === "user-request"
      ? request.requesterUserId
      : undefined);

  const messageId = request.messageId ||
    (payload?.messageId as string | undefined);

  if (!conversationId) return null;
  return viewChatConversationLink(conversationId, messageId);
};

const requestAdminUrl = (requestId: string) => {
  const base = typeof globalThis.location !== "undefined"
    ? globalThis.location.href
    : "https://hi.f0mo.com/admin";
  const url = new URL(base);
  url.pathname = "/admin";
  url.searchParams.set("adminTab", "requests");
  url.searchParams.set("requestId", requestId);
  return url.toString();
};

const targetPendingTab = (request: SourceRequest) =>
  request.kind === "coverage-gap"
    ? "coverage-gaps"
    : request.discoverySource === "user-request"
    ? "user-requested"
    : request.discoverySource === "team-member-agent" ||
        request.discoverySource === "ceo-agent"
    ? "recommended"
    : whatsappDecision(request) === "Recommended"
    ? "recommended"
    : "skipped";

const RequestDetails = (
  {
    request,
    copiedId,
    onCopyLink,
    copiedSourceUrl,
    onCopySourceUrl,
  }: {
    request: SourceRequest;
    copiedId?: string | null;
    onCopyLink: (requestId: string) => void;
    copiedSourceUrl?: string | null;
    onCopySourceUrl: (url: string) => void;
  },
) => {
  const fb = fbPayload(request);
  const cg = coverageGapPayload(request);
  const viewChatUrl = getSourceRequestViewChatLink(request);
  const isRequestCopied = copiedId === request.id;
  const isUrlCopied = copiedSourceUrl === request.sourceUrl;
  return (
    <>
      <div className="space-y-1">
        <DetailRow label="Created" value={formatDate(request.createdAt)} />
        <DetailRow
          label="Discovery"
          value={request.kind === "coverage-gap"
            ? "Coverage Gap"
            : discoverySourceLabel(request.discoverySource)}
        />
        <DetailRow
          label="Discovered in"
          value={whatsappChatTitle(request)}
        />
        {request.discoverySource !== "spy-bot" && (
          <DetailRow label="Requester" value={request.requesterUserId} />
        )}
        <DetailRow label="Requester email" value={request.requesterEmail} />
        {Boolean(request.sourceUrl) && (
          <div className="text-sm text-slate-300 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-slate-400 shrink-0">Source URL:</span>{" "}
              <a
                href={request.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 underline underline-offset-2 break-all"
              >
                {request.sourceUrl}
              </a>
            </div>
            <button
              type="button"
              onClick={() => onCopySourceUrl(request.sourceUrl)}
              className="text-xs text-slate-400 hover:text-white px-2 py-0.5 rounded border border-white/10 hover:bg-white/5 inline-flex items-center gap-1 shrink-0"
              title="Copy source URL"
            >
              {isUrlCopied
                ? <Check size={12} className="text-emerald-400" />
                : <Copy size={12} />}
              <span>{isUrlCopied ? "Copied URL" : "Copy URL"}</span>
            </button>
          </div>
        )}
        {(request.kind === "link-scrape-source" ||
          request.kind === "facebook-source") && (
          <DetailRow label="Source name" value={fb?.title || request.title} />
        )}
        {cg && (
          <>
            <DetailRow label="Query summary" value={cg.querySummary} />
            <DetailRow label="Category" value={cg.category} />
            <DetailRow label="Location" value={cg.location} />
            <DetailRow label="Time context" value={cg.timeContext} />
            <DetailRow label="Notes" value={cg.notes} />
          </>
        )}
        <DetailRow label="Members" value={request.memberCount} />
        {fb?.privacy && <DetailRow label="Privacy" value={fb.privacy} />}
        {fb?.category && <DetailRow label="Category" value={fb.category} />}
        <DetailRow label="AI decision" value={whatsappDecision(request)} />
        <DetailRow label="Recommendation" value={request.recommendation} />
        <DetailRow label="Reviewed" value={formatDate(request.reviewedAt)} />
        <DetailRow label="Reviewer" value={request.reviewerEmail} />
        {request.status === "rejected" && (
          <DetailRow
            label="Rejection Reason"
            value={(request.resolution as
              | { reason?: string; message?: string }
              | undefined)?.reason ||
              (request.resolution as
                | { reason?: string; message?: string }
                | undefined)?.message}
          />
        )}
        <div className="text-sm text-slate-300 flex items-center gap-2 pt-1">
          <span className="text-slate-400">Request link:</span>
          <button
            type="button"
            onClick={() => onCopyLink(request.id)}
            className="inline-flex items-center gap-1.5 text-xs text-purple-300 hover:text-white px-2.5 py-1 rounded-lg border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 transition"
          >
            {isRequestCopied
              ? <Check size={12} className="text-emerald-400" />
              : <Link size={12} />}
            <span>
              {isRequestCopied ? "Copied request link" : "Copy request link"}
            </span>
          </button>
        </div>
        {viewChatUrl && (
          <div className="text-sm text-slate-300 flex items-center gap-1.5 pt-1">
            <span className="text-slate-400">View Chat:</span>
            <a
              href={viewChatUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-purple-400 hover:text-purple-300 underline font-medium"
            >
              Open conversation in View Chat
              <ExternalLink size={14} />
            </a>
          </div>
        )}
      </div>
      {fb?.description && (
        <div
          dir="auto"
          className="rounded-xl border border-white/10 bg-slate-900/60 p-3 text-xs text-slate-300 whitespace-pre-wrap break-words"
        >
          <div className="text-slate-400 font-medium mb-1">
            Group Description:
          </div>
          {fb.description}
        </div>
      )}
      <div
        dir="auto"
        className="rounded-xl border border-white/10 bg-slate-950/40 p-4 text-sm text-slate-200 whitespace-pre-wrap break-words"
      >
        {request.requestText || "No message text provided."}
      </div>
      <SourceScrapePreview request={request} />
    </>
  );
};

const SourceScrapePreview = ({ request }: { request: SourceRequest }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<SourcePreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (request.kind === "coverage-gap" || request.kind === "whatsapp-group") {
    return null;
  }

  const runPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const auth = await db.getAuth();
      const token = auth?.refresh_token;
      if (!token) throw new Error("Authentication token missing.");
      const data = await fetchAdminSourcePreview(token, request.id);
      setPreview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Live Scrape & Parse Preview
        </div>
        <button
          type="button"
          disabled={loading}
          onClick={runPreview}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/40 text-purple-200 transition disabled:opacity-50"
        >
          {loading
            ? (
              <>
                <Spinner className="w-3 h-3 text-purple-300" />
                <span>Checking source...</span>
              </>
            )
            : preview
            ? (
              <>
                <RefreshCw size={12} />
                <span>Re-check Live Source</span>
              </>
            )
            : (
              <>
                <Sparkles size={12} />
                <span>Test Scrape & Check Events</span>
              </>
            )}
        </button>
      </div>

      {error && (
        <div className="text-xs text-rose-400 bg-rose-950/40 border border-rose-800/40 rounded-lg p-2.5">
          {error}
        </div>
      )}

      {preview && (
        <div className="space-y-2.5">
          <div
            className={`flex items-start gap-2 text-xs rounded-lg p-3 border ${
              preview.status === "active" && preview.recentEventCount > 0
                ? "bg-emerald-950/30 border-emerald-500/30 text-emerald-200"
                : preview.status === "unscrapable"
                ? "bg-rose-950/30 border-rose-500/30 text-rose-200"
                : "bg-amber-950/30 border-amber-500/30 text-amber-200"
            }`}
          >
            <div className="font-medium flex-1">
              <div>{preview.summary}</div>
              {preview.reason && (
                <div className="mt-1 text-slate-300 opacity-90 font-normal">
                  {preview.reason}
                </div>
              )}
            </div>
            <span className="font-bold uppercase tracking-wider text-[10px] px-2 py-0.5 rounded-full border border-current shrink-0">
              {preview.status}
            </span>
          </div>

          {preview.autoApproval && (
            <div
              className={`p-2.5 rounded-lg border text-xs flex items-start gap-2 ${
                preview.autoApproval.eligible
                  ? "bg-purple-950/40 border-purple-500/40 text-purple-200"
                  : "bg-slate-900/60 border-white/10 text-slate-400"
              }`}
            >
              <span className="shrink-0 mt-0.5">
                {preview.autoApproval.eligible ? "⚡" : "ℹ️"}
              </span>
              <div className="flex-1 space-y-1">
                <div>
                  <span className="font-semibold">
                    {preview.autoApproval.eligible
                      ? "Auto-Approve Eligible: "
                      : "Manual Review: "}
                  </span>
                  <span>{preview.autoApproval.reason}</span>
                </div>
                {preview.autoApproval.metrics && (
                  <div className="flex flex-wrap gap-2 text-[11px] text-slate-300">
                    {preview.autoApproval.metrics.followerCount !== undefined &&
                      (
                        <span className="bg-white/5 border border-white/10 px-1.5 py-0.5 rounded">
                          Followers: {preview.autoApproval.metrics.followerCount
                            .toLocaleString()}
                        </span>
                      )}
                    {preview.autoApproval.metrics.memberCount !== undefined && (
                      <span className="bg-white/5 border border-white/10 px-1.5 py-0.5 rounded">
                        Members: {preview.autoApproval.metrics.memberCount
                          .toLocaleString()}
                      </span>
                    )}
                    {preview.autoApproval.metrics.eventRatio !== undefined && (
                      <span className="bg-white/5 border border-white/10 px-1.5 py-0.5 rounded">
                        Event Ratio: {Math.round(
                          preview.autoApproval.metrics.eventRatio * 100,
                        )}%
                      </span>
                    )}
                    {preview.autoApproval.metrics.upcomingEventCount !==
                        undefined && (
                      <span className="bg-white/5 border border-white/10 px-1.5 py-0.5 rounded">
                        Upcoming Events:{" "}
                        {preview.autoApproval.metrics.upcomingEventCount}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {preview.events && preview.events.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-slate-300 font-medium">
                Detected Events ({preview.events.length}):
              </div>
              <div className="grid gap-2 max-h-60 overflow-y-auto pr-1">
                {preview.events.map((e, idx) => (
                  <div
                    key={idx}
                    className="p-2.5 rounded-lg border border-white/10 bg-slate-900/80 text-xs space-y-1"
                  >
                    <div className="font-semibold text-white flex items-center justify-between gap-2">
                      <span dir="auto" className="truncate">{e.name}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {e.isUpcoming !== undefined && (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              e.isUpcoming
                                ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                                : "bg-slate-800 text-slate-400 border border-white/10"
                            }`}
                          >
                            {e.isUpcoming ? "Upcoming" : "Past date"}
                          </span>
                        )}
                        {e.datetimeStart && (
                          <span className="text-purple-300 font-normal">
                            {e.datetimeStart}
                          </span>
                        )}
                      </div>
                    </div>
                    {(e.venue || e.city) && (
                      <div className="text-slate-400" dir="auto">
                        📍 {[e.venue, e.city].filter(Boolean).join(", ")}
                      </div>
                    )}
                    {e.description && (
                      <div className="text-slate-300 line-clamp-2" dir="auto">
                        {e.description}
                      </div>
                    )}
                    {e.categories && e.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-0.5">
                        {e.categories.map((cat) => (
                          <span
                            key={cat}
                            className="px-1.5 py-0.5 rounded text-[10px] bg-white/5 border border-white/10 text-slate-300"
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ActionButtons = (
  { busy, onApprove, onReject, approveLabel }: {
    busy: boolean;
    onApprove: () => void;
    onReject: (reason?: string) => void;
    approveLabel?: string;
  },
) => {
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [reason, setReason] = useState("");

  if (showRejectInput) {
    return (
      <div className="flex flex-col gap-2 w-full max-w-md">
        <textarea
          placeholder="Reason for rejection (will be sent back to user)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-xl border border-white/20 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
          rows={2}
        />
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => {
              setShowRejectInput(false);
              setReason("");
            }}
            className="rounded-xl border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              onReject(reason.trim() || undefined);
            }}
            className="flex items-center gap-1.5 rounded-xl bg-red-500/20 px-3 py-1.5 text-xs text-red-100 border border-red-500/30 disabled:opacity-50"
          >
            <X size={14} /> Confirm Reject
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3">
      <button
        type="button"
        disabled={busy}
        onClick={onApprove}
        className="flex items-center gap-2 rounded-xl bg-emerald-500/20 px-4 py-2 text-emerald-100 border border-emerald-500/30 disabled:opacity-50"
      >
        <Check size={16} /> {approveLabel || "Approve"}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => setShowRejectInput(true)}
        className="flex items-center gap-2 rounded-xl bg-red-500/20 px-4 py-2 text-red-100 border border-red-500/30 disabled:opacity-50"
      >
        <X size={16} /> Reject
      </button>
    </div>
  );
};

const ClusterCard = (
  {
    cluster,
    busyId,
    onAction,
    targetRequestId,
    copiedId,
    onCopyLink,
    copiedSourceUrl,
    onCopySourceUrl,
  }: {
    cluster: SourceRequest[];
    busyId: string | null;
    onAction: (
      cluster: SourceRequest[],
      primaryId: string,
      action: "approve" | "reject",
      reason?: string,
    ) => void;
    targetRequestId?: string | null;
    copiedId?: string | null;
    onCopyLink: (requestId: string) => void;
    copiedSourceUrl?: string | null;
    onCopySourceUrl: (url: string) => void;
  },
) => {
  const isTarget = useMemo(
    () =>
      Boolean(targetRequestId && cluster.some((r) => r.id === targetRequestId)),
    [cluster, targetRequestId],
  );
  const targetIndex = useMemo(() => {
    if (!targetRequestId) return 0;
    const foundIndex = cluster.findIndex((r) => r.id === targetRequestId);
    return foundIndex >= 0 ? foundIndex : 0;
  }, [cluster, targetRequestId]);

  const [expanded, setExpanded] = useState(isTarget);
  const [activeIndex, setActiveIndex] = useState(targetIndex);

  useEffect(() => {
    if (isTarget) {
      setExpanded(true);
      setActiveIndex(targetIndex);
    }
  }, [isTarget, targetIndex]);

  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isTarget && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isTarget]);

  const primary = cluster[activeIndex] ?? cluster[0];
  const status = primary.status as Status;
  return (
    <div
      ref={cardRef}
      className={`rounded-2xl border transition-all duration-300 ${
        isTarget
          ? "border-purple-500/80 bg-purple-950/20 ring-2 ring-purple-500/40"
          : "border-white/10 bg-white/5"
      } backdrop-blur-sm shadow-xl`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/5"
      >
        <div className="flex items-center gap-3 min-w-0">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400">
              {kindLabel(primary.kind)}
              {cluster.length > 1 && ` · ${cluster.length} variants`}
            </div>
            <div className="font-semibold text-white truncate">
              {payloadSummary(primary)}
              {whatsappChatTitle(primary) && (
                <span className="text-xs text-slate-400 font-normal ml-2">
                  from {whatsappChatTitle(primary)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button
            type="button"
            title="Copy link to requested source"
            onClick={(e) => {
              e.stopPropagation();
              onCopyLink(primary.id);
            }}
            className="p-1.5 rounded-lg border border-white/10 text-slate-300 hover:text-white hover:bg-white/10 transition flex items-center gap-1.5 text-xs"
          >
            {copiedId === primary.id
              ? <Check size={14} className="text-emerald-400" />
              : <Link size={14} className="text-purple-300" />}
            <span className="hidden sm:inline">
              {copiedId === primary.id ? "Copied" : "Copy link"}
            </span>
          </button>
          {primary.kind === "coverage-gap"
            ? (
              <span className="text-xs rounded-full border px-2 py-0.5 border-rose-500/30 bg-rose-500/10 text-rose-200">
                Coverage Gap
              </span>
            )
            : discoverySourceLabel(primary.discoverySource) && (
              <span
                className={`text-xs rounded-full border px-2 py-0.5 ${
                  primary.discoverySource === "spy-bot"
                    ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-200"
                    : "border-purple-500/30 bg-purple-500/10 text-purple-200"
                }`}
              >
                {discoverySourceLabel(primary.discoverySource)}
              </span>
            )}
          {primary.memberCount != null && (
            <span className="text-xs text-slate-400">
              {primary.memberCount} members
            </span>
          )}
          <span
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              statusLabelClass[status]
            }`}
          >
            {status}
          </span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-white/10 px-5 py-4 space-y-4">
          {cluster.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {cluster.map((r, i) => (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => setActiveIndex(i)}
                  className={`rounded-lg border px-3 py-1 text-xs ${
                    i === activeIndex
                      ? "border-white/40 bg-white/10 text-white font-medium"
                      : "border-white/10 text-slate-300 hover:bg-white/5"
                  }`}
                >
                  <span dir="auto">{r.title || r.sourceUrl}</span>
                </button>
              ))}
            </div>
          )}
          <RequestDetails
            request={primary}
            copiedId={copiedId}
            onCopyLink={onCopyLink}
            copiedSourceUrl={copiedSourceUrl}
            onCopySourceUrl={onCopySourceUrl}
          />
          {status === "pending" && (
            <div className="flex items-center justify-between">
              {whatsappDecision(primary) && (
                <span
                  className={`text-xs rounded-full border px-2 py-0.5 ${
                    whatsappDecision(primary) === "Recommended"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                      : "border-amber-500/30 bg-amber-500/10 text-amber-200"
                  }`}
                >
                  AI: {whatsappDecision(primary)}
                </span>
              )}
              <ActionButtons
                busy={busyId === primary.id}
                onApprove={() => onAction(cluster, primary.id, "approve")}
                onReject={(reason) =>
                  onAction(cluster, primary.id, "reject", reason)}
                approveLabel={primary.kind === "coverage-gap"
                  ? "Mark Reviewed"
                  : "Approve"}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const useAutoLoadNext = (
  canLoadNextPage: boolean,
  loadNextPage: () => void,
) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !canLoadNextPage) return;
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) =>
        entries.some((e) => e.isIntersecting) && loadNextPage(),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [canLoadNextPage, loadNextPage]);
  return sentinelRef;
};

const pageSize = 20;

const RequestsSection = (
  {
    status,
    busyId,
    hiddenIds,
    onAction,
    recommendationFilter,
    targetRequestId,
    targetRequest,
    copiedId,
    onCopyLink,
    copiedSourceUrl,
    onCopySourceUrl,
  }: {
    status: string;
    busyId: string | null;
    hiddenIds: Set<string>;
    onAction: (
      cluster: SourceRequest[],
      primaryId: string,
      action: "approve" | "reject",
      reason?: string,
    ) => void;
    recommendationFilter?:
      | "user-requested"
      | "coverage-gaps"
      | "recommended"
      | "skipped";
    targetRequestId?: string | null;
    targetRequest?: SourceRequest | null;
    copiedId?: string | null;
    onCopyLink: (requestId: string) => void;
    copiedSourceUrl?: string | null;
    onCopySourceUrl: (url: string) => void;
  },
) => {
  const { data, isLoading, error, canLoadNextPage, loadNextPage } = db
    .useInfiniteQuery({
      sourceRequests: {
        $: {
          where: { status },
          order: { createdAt: "desc" },
          limit: pageSize,
        },
      },
    });
  const queryRequests = ((data?.sourceRequests ?? []) as SourceRequest[])
    .filter(
      (r) => !hiddenIds.has(r.id),
    );
  const requests = useMemo(() => {
    if (
      targetRequest &&
      targetRequest.status === status &&
      !hiddenIds.has(targetRequest.id) &&
      !queryRequests.some((r) => r.id === targetRequest.id)
    ) {
      return [targetRequest, ...queryRequests];
    }
    return queryRequests;
  }, [queryRequests, targetRequest, status, hiddenIds]);
  const clusters = useMemo(
    () => {
      const raw = status === "pending"
        ? clusterRequests(requests)
        : requests.map((r) => [r]);
      if (!recommendationFilter) return raw;
      return raw.filter((c) => {
        const primary = c[0];
        if (recommendationFilter === "coverage-gaps") {
          return primary.kind === "coverage-gap";
        }
        if (recommendationFilter === "user-requested") {
          return primary.discoverySource === "user-request" &&
            primary.kind !== "coverage-gap";
        }
        if (
          primary.discoverySource === "user-request" ||
          primary.kind === "coverage-gap"
        ) {
          return false;
        }
        if (
          primary.discoverySource === "team-member-agent" ||
          primary.discoverySource === "ceo-agent"
        ) {
          return recommendationFilter === "recommended";
        }
        const decision = whatsappDecision(primary as SourceRequest);
        return recommendationFilter === "recommended"
          ? decision === "Recommended"
          : decision !== "Recommended";
      });
    },
    [requests, status, recommendationFilter],
  );
  const sentinelRef = useAutoLoadNext(canLoadNextPage, loadNextPage);
  const targetBucketLabel = useMemo(() => {
    if (!targetRequestId) return null;
    const targetCluster = clusters.find((c) =>
      c.some((r) => r.id === targetRequestId)
    );
    return targetCluster ? getBucketLabel(targetCluster[0]?.createdAt) : null;
  }, [clusters, targetRequestId]);

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    Today: true,
    Yesterday: true,
  });

  useEffect(() => {
    if (targetBucketLabel) {
      setExpandedSections((prev) => ({ ...prev, [targetBucketLabel]: true }));
    }
  }, [targetBucketLabel]);

  const groupedBuckets = useMemo(
    () => groupClustersByDate(clusters),
    [clusters],
  );
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-medium capitalize text-white">
          {recommendationFilter
            ? recommendationFilter === "user-requested"
              ? "User Sources"
              : recommendationFilter === "coverage-gaps"
              ? "Coverage Gaps"
              : recommendationFilter === "recommended"
              ? "AI Recommended"
              : "AI Skipped"
            : status}
        </h2>
        <div className="text-sm text-gray-500">
          {clusters.length} {status === "pending" ? "groups" : "items"}
          {requests.length !== clusters.length && ` (${requests.length} total)`}
        </div>
      </div>
      {error && <div className="text-red-400">{error.message}</div>}
      {isLoading
        ? <Spinner />
        : requests.length === 0
        ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-gray-500">
            No {status} requests.
          </div>
        )
        : (
          <div className="space-y-6">
            {groupedBuckets.map(({ label, clusters: bucketClusters }) => {
              const isExpanded = expandedSections[label] ??
                (label === "Today" || label === "Yesterday");
              return (
                <div key={label} className="space-y-3">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedSections((prev) => ({
                        ...prev,
                        [label]: !isExpanded,
                      }))}
                    className="flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white transition w-full text-left py-1 border-b border-white/5"
                  >
                    {isExpanded
                      ? <ChevronDown size={14} />
                      : <ChevronRight size={14} />}
                    <span>{label}</span>
                    <span className="text-xs text-slate-500 bg-white/5 px-1.5 py-0.5 rounded-full ml-1">
                      {bucketClusters.length}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="space-y-3">
                      {bucketClusters.map((cluster) => (
                        <ClusterCard
                          key={cluster[0].id}
                          cluster={cluster}
                          busyId={busyId}
                          onAction={onAction}
                          targetRequestId={targetRequestId}
                          copiedId={copiedId}
                          onCopyLink={onCopyLink}
                          copiedSourceUrl={copiedSourceUrl}
                          onCopySourceUrl={onCopySourceUrl}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={sentinelRef} className="h-8" />
            {canLoadNextPage && (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            )}
          </div>
        )}
    </section>
  );
};

const InstagramFollowingImporter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [usernameInput, setUsernameInput] = useState("");
  const [cityHint, setCityHint] = useState("");
  const [directAddToCoverage, setDirectAddToCoverage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FollowingScanResult | null>(null);
  const [showCandidatesList, setShowCandidatesList] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usernameInput.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const auth = await db.getAuth();
      const token = auth?.refresh_token;
      if (!token) throw new Error("Missing auth token. Please log in again.");
      const res = await scanInstagramFollowing(token, {
        username: usernameInput.trim(),
        cityHint: cityHint.trim() || undefined,
        directAddToCoverage,
      });
      setResult(res);
      setShowCandidatesList(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Scan failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-purple-500/20 via-pink-500/20 to-amber-500/20 border border-purple-500/30 text-purple-300">
            <Sparkles size={20} />
          </div>
          <div>
            <h3 className="font-medium text-white text-base">
              Import Event Sources from Instagram Following
            </h3>
            <p className="text-xs text-slate-400">
              Enter any Instagram profile to extract their followed event
              venues, clubs, and collectives.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:bg-white/5 transition flex items-center gap-1.5"
        >
          <span>{isOpen ? "Hide Importer" : "Open Importer"}</span>
          <ChevronDown
            size={14}
            className={`transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {isOpen && (
        <div className="mt-5 pt-5 border-t border-white/10 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">
                  Target Instagram Profile
                </label>
                <input
                  type="text"
                  required
                  disabled={loading}
                  placeholder="e.g. nastya.solo or https://instagram.com/arimeridy"
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-purple-500 focus:outline-none transition"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">
                  City / Scene Hint (Optional)
                </label>
                <input
                  type="text"
                  disabled={loading}
                  placeholder="e.g. Berlin, New York, Tel Aviv"
                  value={cityHint}
                  onChange={(e) => setCityHint(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-purple-500 focus:outline-none transition"
                />
              </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-4 pt-1">
              <label className="flex items-center gap-2.5 cursor-pointer select-none text-xs text-slate-300">
                <input
                  type="checkbox"
                  disabled={loading}
                  checked={directAddToCoverage}
                  onChange={(e) => setDirectAddToCoverage(e.target.checked)}
                  className="h-4 w-4 rounded border-white/20 bg-black/50 text-purple-600 focus:ring-purple-500"
                />
                <span>
                  Directly add extracted event profiles to active database
                  coverage
                </span>
              </label>

              <button
                type="submit"
                disabled={loading || !usernameInput.trim()}
                className="rounded-xl border border-purple-500/40 bg-purple-600/30 hover:bg-purple-600/40 text-purple-100 font-medium px-5 py-2.5 text-sm transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-purple-900/20"
              >
                {loading
                  ? (
                    <>
                      <Spinner className="h-4 w-4 text-purple-300" />
                      <span>Scanning & Extracting...</span>
                    </>
                  )
                  : (
                    <>
                      <Sparkles size={16} />
                      <span>Scan & Extract Event Sources</span>
                    </>
                  )}
              </button>
            </div>
          </form>

          {loading && (
            <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4 flex items-center gap-3 text-xs text-purple-200">
              <Spinner className="h-4 w-4 shrink-0 text-purple-400" />
              <span>
                Scanning public followed accounts and classifying event profiles
                with AI. For accounts with many followers, this may take up to a
                minute...
              </span>
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-xs text-red-200">
              {error}
            </div>
          )}

          {result && (
            <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm font-medium text-white flex items-center gap-2">
                  <Check size={16} className="text-emerald-400" />
                  <span>
                    Scan completed for @{result.scannedUsername}{" "}
                    ({result.followingCount} followed accounts,{" "}
                    {result.followingCount - result.privateSkipped} public)
                  </span>
                </div>
                {result.candidates.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowCandidatesList((prev) => !prev)}
                    className="text-xs text-purple-300 hover:text-purple-200 transition underline"
                  >
                    {showCandidatesList
                      ? "Hide Discovered Profiles"
                      : `View ${result.candidates.length} Profiles`}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
                <div className="rounded-lg border border-white/5 bg-white/5 p-2.5">
                  <div className="text-lg font-semibold text-white">
                    {result.candidatesFound}
                  </div>
                  <div className="text-slate-400 mt-0.5">Event Candidates</div>
                </div>

                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2.5">
                  <div className="text-lg font-semibold text-emerald-300">
                    {result.addedToCoverage.length}
                  </div>
                  <div className="text-emerald-400/80 mt-0.5">
                    Added to Coverage
                  </div>
                </div>

                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5">
                  <div className="text-lg font-semibold text-amber-300">
                    {result.createdRequests.length}
                  </div>
                  <div className="text-amber-400/80 mt-0.5">Pending Review</div>
                </div>

                <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-2.5">
                  <div className="text-lg font-semibold text-blue-300">
                    {result.alreadyCovered.length +
                      result.alreadyPending.length}
                  </div>
                  <div className="text-blue-400/80 mt-0.5">
                    Already In System
                  </div>
                </div>
              </div>

              {showCandidatesList && result.candidates.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-2 max-h-80 overflow-y-auto pr-1">
                  {result.candidates.map((c) => {
                    const isAdded = result.addedToCoverage.includes(c.username);
                    const isPending = result.createdRequests.includes(
                      c.username,
                    );
                    const isExisting =
                      result.alreadyCovered.includes(c.username) ||
                      result.alreadyPending.includes(c.username);
                    return (
                      <div
                        key={c.username}
                        className="rounded-lg border border-white/5 bg-white/5 p-2.5 flex items-start justify-between gap-3 text-xs"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <a
                              href={`https://www.instagram.com/${c.username}/`}
                              target="_blank"
                              rel="noreferrer"
                              className="font-medium text-white hover:text-purple-300 transition flex items-center gap-1"
                            >
                              <span>@{c.username}</span>
                              <ExternalLink
                                size={10}
                                className="text-slate-400"
                              />
                            </a>
                            {c.name && (
                              <span className="text-slate-400 truncate">
                                · {c.name}
                              </span>
                            )}
                          </div>
                          {c.reason && (
                            <p className="text-slate-300 mt-1 leading-relaxed">
                              {c.reason}
                            </p>
                          )}
                        </div>

                        <div className="shrink-0">
                          {isAdded && (
                            <span className="rounded-full bg-emerald-500/20 border border-emerald-500/30 px-2 py-0.5 text-[10px] text-emerald-200">
                              Added
                            </span>
                          )}
                          {isPending && (
                            <span className="rounded-full bg-amber-500/20 border border-amber-500/30 px-2 py-0.5 text-[10px] text-amber-200">
                              Requested
                            </span>
                          )}
                          {isExisting && (
                            <span className="rounded-full bg-blue-500/20 border border-blue-500/30 px-2 py-0.5 text-[10px] text-blue-200">
                              Already Covered
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const AdminRequests = () => {
  const user = db.useAuth().user;
  const [searchParams] = useSearchParams();
  const targetRequestId = searchParams.get("requestId");
  const { data: targetData } = db.useQuery(
    targetRequestId
      ? {
        sourceRequests: {
          $: {
            where: { id: targetRequestId },
          },
        },
      }
      : null,
  );
  const targetRequest = (targetData?.sourceRequests?.[0] ?? null) as
    | SourceRequest
    | null;

  const [activeStatus, setActiveStatus] = useState<Status>("pending");
  const [pendingTab, setPendingTab] = useState<
    "user-requested" | "coverage-gaps" | "recommended" | "skipped"
  >("user-requested");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedSourceUrl, setCopiedSourceUrl] = useState<string | null>(null);
  const [autoApproving, setAutoApproving] = useState(false);

  const handleAutoApproveAll = async () => {
    setAutoApproving(true);
    try {
      const auth = await db.getAuth();
      const token = auth?.refresh_token;
      if (!token) throw new Error("Missing auth token.");
      const result = await fetchAdminAutoApprovePending(token);
      pushToast({
        kind: "success",
        title: "Auto-approval complete",
        description:
          `Auto-approved ${result.autoApprovedCount} of ${result.totalPending} pending requests.`,
      });
    } catch (err) {
      pushToast({
        kind: "error",
        title: "Auto-approval failed",
        description: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setAutoApproving(false);
    }
  };

  useEffect(() => {
    if (!targetRequest) return;
    if (
      targetRequest.status &&
      statuses.includes(targetRequest.status as Status)
    ) {
      setActiveStatus(targetRequest.status as Status);
    }
    if (targetRequest.status === "pending") {
      setPendingTab(targetPendingTab(targetRequest));
    }
  }, [targetRequest]);

  const pushToast = (toast: Omit<Toast, "id">) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((current) => [...current, { ...toast, id }]);
    if (!toast.pending) {
      setTimeout(
        () => setToasts((current) => current.filter((item) => item.id !== id)),
        3500,
      );
    }
    return id;
  };

  const replaceToast = (id: number, toast: Omit<Toast, "id">) => {
    setToasts((current) =>
      current.map((item) => item.id === id ? { ...toast, id } : item)
    );
    setTimeout(
      () => setToasts((current) => current.filter((item) => item.id !== id)),
      3500,
    );
  };

  const copyLink = (requestId: string) => {
    const url = requestAdminUrl(requestId);
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(requestId);
      setTimeout(() => setCopiedId(null), 2000);
      pushToast({
        kind: "success",
        title: "Link copied to clipboard",
        description: url,
      });
    });
  };

  const copySourceUrl = (sourceUrl: string) => {
    navigator.clipboard.writeText(sourceUrl).then(() => {
      setCopiedSourceUrl(sourceUrl);
      setTimeout(() => setCopiedSourceUrl(null), 2000);
      pushToast({
        kind: "success",
        title: "Source URL copied to clipboard",
        description: sourceUrl,
      });
    });
  };

  const act = async (
    cluster: SourceRequest[],
    requestId: string,
    action: "approve" | "reject",
    reason?: string,
  ) => {
    const auth = await db.getAuth();
    const token = auth?.refresh_token;
    if (!token) {
      setMessage("Missing auth token");
      return;
    }
    const clusterIds = cluster.map((r) => r.id);
    setHiddenIds((prev) => new Set([...prev, ...clusterIds]));
    setBusyId(requestId);
    setMessage("");
    const toastId = pushToast({
      kind: "info",
      pending: true,
      title: action === "approve" ? "Approving request" : "Rejecting request",
    });
    const promise = action === "approve"
      ? approveAdminSourceRequest(token, requestId)
      : rejectAdminSourceRequest(token, requestId, reason);
    promise
      .then(() =>
        replaceToast(toastId, {
          kind: "success",
          title: action === "approve" ? "Request approved" : "Request rejected",
        })
      )
      .catch((err: unknown) => {
        setHiddenIds((prev) => {
          const next = new Set(prev);
          clusterIds.forEach((id) => next.delete(id));
          return next;
        });
        const errorMessage = err instanceof Error
          ? err.message
          : `Failed to ${action} request`;
        setMessage(errorMessage);
        replaceToast(toastId, {
          kind: "error",
          title: action === "approve"
            ? "Failed to approve request"
            : "Failed to reject request",
          description: errorMessage,
        });
      })
      .finally(() => setBusyId(null));
  };

  if (!user) return <Spinner />;

  return (
    <div className="space-y-6">
      <ToastViewport toasts={toasts} />
      {message && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-100">
          {message}
        </div>
      )}
      <InstagramFollowingImporter />
      <div className="flex gap-2">
        {statuses.map((status) => (
          <button
            type="button"
            key={status}
            onClick={() => setActiveStatus(status)}
            className={`rounded-xl border px-4 py-2 text-sm capitalize ${
              activeStatus === status
                ? "border-white/40 bg-white/10 text-white"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            {status}
          </button>
        ))}
      </div>
      {activeStatus === "pending" && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setPendingTab("user-requested")}
            className={`rounded-lg border px-3 py-1 text-xs ${
              pendingTab === "user-requested"
                ? "border-purple-500/30 bg-purple-500/10 text-purple-200"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            User Sources
          </button>
          <button
            type="button"
            onClick={() => setPendingTab("coverage-gaps")}
            className={`rounded-lg border px-3 py-1 text-xs ${
              pendingTab === "coverage-gaps"
                ? "border-rose-500/30 bg-rose-500/10 text-rose-200"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            Coverage Gaps
          </button>
          <button
            type="button"
            onClick={() => setPendingTab("recommended")}
            className={`rounded-lg border px-3 py-1 text-xs ${
              pendingTab === "recommended"
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            AI Recommended
          </button>
          <button
            type="button"
            onClick={() => setPendingTab("skipped")}
            className={`rounded-lg border px-3 py-1 text-xs ${
              pendingTab === "skipped"
                ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                : "border-white/10 text-slate-300 hover:bg-white/5"
            }`}
          >
            AI Skipped
          </button>
          <button
            type="button"
            disabled={autoApproving}
            onClick={handleAutoApproveAll}
            className="ml-auto rounded-lg border border-purple-500/40 bg-purple-600/20 hover:bg-purple-600/30 text-purple-200 px-3 py-1 text-xs font-medium inline-flex items-center gap-1.5 transition disabled:opacity-50"
          >
            {autoApproving
              ? <Spinner className="w-3 h-3 text-purple-300" />
              : <Sparkles size={12} />}
            <span>Auto-Approve Eligible</span>
          </button>
        </div>
      )}
      <RequestsSection
        key={`${activeStatus}-${pendingTab}`}
        status={activeStatus}
        busyId={busyId}
        hiddenIds={hiddenIds}
        onAction={act}
        recommendationFilter={activeStatus === "pending"
          ? pendingTab
          : undefined}
        targetRequestId={targetRequestId}
        targetRequest={targetRequest}
        copiedId={copiedId}
        onCopyLink={copyLink}
        copiedSourceUrl={copiedSourceUrl}
        onCopySourceUrl={copySourceUrl}
      />
    </div>
  );
};

const useSourceEvents = (selectedSource: SelectedSource | null) => {
  const [events, setEvents] = useState<EventEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedSource) return;
    const mounted = { current: true };
    setLoading(true);
    setError(null);
    db.getAuth()
      .then((auth) => {
        if (!auth?.refresh_token) throw new Error("Missing auth token");
        return fetchAdminSourceEvents(
          auth.refresh_token,
          selectedSource.sourceKind,
          selectedSource.sourceRef,
        );
      })
      .then((result) => {
        if (mounted.current) setEvents(result);
      })
      .catch((err) => {
        if (mounted.current) {
          setError(
            err instanceof Error ? err.message : "Failed loading events",
          );
        }
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
    return () => {
      mounted.current = false;
    };
  }, [selectedSource?.sourceKind, selectedSource?.sourceRef]);

  return { events, loading, error };
};

const ExpandableSourceCard = (
  { selectedSource, header }: {
    selectedSource: SelectedSource;
    header: React.ReactNode;
  },
) => {
  const [expanded, setExpanded] = useState(false);
  const { events, loading, error } = useSourceEvents(
    expanded ? selectedSource : null,
  );
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/10 transition"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <div className="min-w-0 flex-1">{header}</div>
      </button>
      {expanded && (
        <div className="border-t border-white/10 px-4 py-3 space-y-2 bg-slate-950/40">
          {loading
            ? <Spinner className="h-4 w-4" />
            : error
            ? <div className="text-red-400 text-sm">{error}</div>
            : events.length === 0
            ? (
              <div className="text-gray-500 text-sm">
                No ingested events yet.
              </div>
            )
            : (
              <>
                <div className="text-xs text-gray-500">
                  {events.length} events
                </div>
                {events.map((event) => (
                  <SourceEventRow key={event.id} event={event} />
                ))}
              </>
            )}
        </div>
      )}
    </div>
  );
};

const CoverageColumn = (
  { title, icon, count, children }: {
    title: string;
    icon: React.ReactNode;
    count: number;
    children: React.ReactNode;
  },
) => (
  <section className="space-y-3 min-w-0">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-xl font-medium text-white">{title}</h2>
      </div>
      <div className="text-sm text-gray-500">{count}</div>
    </div>
    {children}
  </section>
);

export const AdminCoverage = () => {
  const { data, isLoading, error } = db.useQuery({
    instagramSources: { $: { where: { status: "active" } } },
    whatsappGroups: {},
    linkScrapeSources: { $: { where: { status: "active" } } },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-red-400">
        Error loading sources: {error.message}
      </div>
    );
  }

  const instagram = data?.instagramSources ?? [];
  const whatsapp = data?.whatsappGroups ?? [];
  const linkSources = data?.linkScrapeSources ?? [];

  return (
    <div className="space-y-6">
      <InstagramFollowingImporter />
      <div className="grid gap-6 lg:grid-cols-3">
        <CoverageColumn
          title="Instagram Pages"
          icon={<Globe size={20} className="text-purple-400" />}
          count={instagram.length}
        >
          {instagram.length === 0
            ? (
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-gray-500 text-sm">
                No active Instagram sources.
              </div>
            )
            : (
              <div className="space-y-2">
                {instagram.map((s) => (
                  <ExpandableSourceCard
                    key={s.id}
                    selectedSource={{
                      label: `@${s.username}`,
                      sourceKind: "instagram-page",
                      sourceRef: s.url ??
                        `https://www.instagram.com/${s.username}`,
                    }}
                    header={
                      <>
                        <div className="font-medium text-white truncate">
                          @{s.username}
                        </div>
                        {s.categories && s.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {s.categories.map((c) => (
                              <span
                                key={c}
                                className="rounded-full bg-purple-500/20 border border-purple-500/30 px-2 py-0.5 text-xs text-purple-200"
                              >
                                {c}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          added by {s.addedBy}
                        </div>
                      </>
                    }
                  />
                ))}
              </div>
            )}
        </CoverageColumn>

        <CoverageColumn
          title="WhatsApp Groups"
          icon={<MessageCircle size={20} className="text-emerald-400" />}
          count={whatsapp.length}
        >
          {whatsapp.length === 0
            ? (
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-gray-500 text-sm">
                No WhatsApp groups.
              </div>
            )
            : (
              <div className="space-y-2">
                {whatsapp.map((g) =>
                  g.whatsappId
                    ? (
                      <ExpandableSourceCard
                        key={g.id}
                        selectedSource={{
                          label: g.name ?? g.whatsappId,
                          sourceKind: "whatsapp-group",
                          sourceRef: g.whatsappId,
                        }}
                        header={
                          <>
                            <div
                              dir="auto"
                              className="font-medium text-white truncate"
                            >
                              {g.name ?? g.whatsappId}
                            </div>
                            {g.memberCount != null && (
                              <div className="text-xs text-gray-400 mt-1">
                                {g.memberCount} members
                              </div>
                            )}
                            {g.description && (
                              <div
                                dir="auto"
                                className="text-xs text-gray-500 line-clamp-2 mt-1"
                              >
                                {g.description}
                              </div>
                            )}
                          </>
                        }
                      />
                    )
                    : null
                )}
              </div>
            )}
        </CoverageColumn>

        <CoverageColumn
          title="Link Sources"
          icon={<Globe size={20} className="text-cyan-400" />}
          count={linkSources.length}
        >
          {linkSources.length === 0
            ? (
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-gray-500 text-sm">
                No active link sources.
              </div>
            )
            : (
              <div className="space-y-2">
                {linkSources.map((s) => (
                  <ExpandableSourceCard
                    key={s.id}
                    selectedSource={{
                      label: s.source,
                      sourceKind: "link-scrape-source",
                      sourceRef: s.source,
                    }}
                    header={
                      <>
                        <div className="font-medium text-white truncate">
                          {s.source}
                        </div>
                        <div className="text-xs text-gray-500 truncate mt-1">
                          {s.url}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {s.useProxy && (
                            <span className="rounded-full bg-cyan-500/20 border border-cyan-500/30 px-2 py-0.5 text-xs text-cyan-200">
                              proxy
                            </span>
                          )}
                          {s.categories?.map((c) => (
                            <span
                              key={c}
                              className="rounded-full bg-purple-500/20 border border-purple-500/30 px-2 py-0.5 text-xs text-purple-200"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      </>
                    }
                  />
                ))}
              </div>
            )}
        </CoverageColumn>
      </div>
    </div>
  );
};
